import React, { useEffect, useState } from "react";
import { getOpenCV } from "./vision/opencv";
import { ETH3D_SCENES, loadETH3DScene } from "./vision/eth3d";
import { KITTI_SCENES, loadKITTIScene } from "./vision/kitti";
import useReconstructionPipeline from "./hooks/useReconstructionPipeline";
import MatchView from "./components/MatchView";
import "./ReconstructionDemo.css";
import { debugLog } from "../../utils/debug";

const DATASET_SCENES = [...ETH3D_SCENES, ...KITTI_SCENES];

const DEFAULT_SETTINGS = {
  featureCount: 1500,
  ratioThreshold: 0.75,
};

const RECONSTRUCTION_STAGES = new Set([
  "input",
  "features",
  "matches",
  "epipolar",
  "reconstruction",
]);

const RECONSTRUCTION_VIEWS = new Set([
  "point-cloud",
  "dense",
  "disparity",
  "match-status",
]);

const RECONSTRUCTION_INFO = {
  input: {
    eyebrow: "TWO-VIEW RECONSTRUCTION · INPUT",
    title: "Stereo Input",
    description:
      "The reconstruction pipeline begins with two views of the same scene. Calibrated datasets provide known camera intrinsics and stereo geometry, while custom image pairs require the relationship between the two cameras to be estimated from visual correspondences.",
    implementation: [
      {
        text: "Supports calibrated KITTI and ETH3D stereo pairs",
        links: [
          {
            term: "KITTI",
            url: "https://www.cvlibs.net/datasets/kitti/",
          },
          {
            term: "ETH3D",
            url: "https://www.eth3d.net/",
          },
        ],
      },
      "Supports arbitrary user-supplied image pairs",
      "Input and reconstruction settings are staged until RUN RECONSTRUCTION is pressed",
      "Calibrated datasets can recover metric depth; arbitrary custom pairs produce reconstruction only up to an unknown projective or scale relationship",
    ],
    pipeline: ["Image Pair", "Calibration / Estimation", "Feature Extraction"],
  },

  features: {
    eyebrow: "TWO-VIEW RECONSTRUCTION · FEATURES",
    title: "ORB Feature Extraction",
    description:
      "Distinctive keypoints are detected independently in both images and represented using ORB binary descriptors. These local features provide repeatable visual landmarks that can be compared across the two camera views.",
    implementation: [
      {
        text: "ORB extraction runs through OpenCV.js in the browser",
        links: [
          {
            term: "ORB",
            url: "https://ieeexplore.ieee.org/document/6126544/",
          },
          {
            term: "OpenCV.js",
            url: "https://docs.opencv.org/4.x/d5/d10/tutorial_js_root.html",
          },
        ],
      },
      "The feature budget is user-adjustable",
      {
        text: "Each ORB keypoint is paired with a compact binary descriptor",
        links: [
          {
            term: "ORB",
            url: "https://ieeexplore.ieee.org/document/6126544/",
          },
        ],
      },
      "All image processing remains client-side",
    ],
    pipeline: [
      "Image A / B",
      "ORB Detection",
      "Binary Descriptors",
      "Keypoints",
    ],
  },

  matches: {
    eyebrow: "TWO-VIEW RECONSTRUCTION · MATCHING",
    title: "Feature Correspondence",
    description:
      "Binary descriptors from the two views are compared to identify candidate correspondences. Ambiguous candidates are removed before geometric estimation so that the later stages operate on a cleaner set of image-point matches.",
    implementation: [
      "Custom descriptor-matching pipeline",
      {
        text: "Hamming distance compares ORB binary descriptors",
        links: [
          {
            term: "ORB",
            url: "https://ieeexplore.ieee.org/document/6126544/",
          },
        ],
      },
      {
        text: "An adjustable nearest-neighbor ratio test rejects ambiguous descriptor matches",
        links: [
          {
            term: "ratio test",
            url: "https://doi.org/10.1023/B:VISI.0000029664.99615.94",
          },
        ],
      },
      "Accepted correspondences feed the robust geometry-estimation stage",
    ],
    pipeline: [
      "ORB Descriptors",
      "Hamming Distance",
      "Ratio Filtering",
      "Correspondences",
    ],
  },

  epipolar: {
    eyebrow: "TWO-VIEW RECONSTRUCTION · GEOMETRY",
    title: "Epipolar Geometry",
    description:
      "Matched image points constrain the relative geometry of the two views. The estimated fundamental matrix describes this relationship, allowing geometrically inconsistent matches to be rejected and corresponding epipolar constraints to be visualized.",
    implementation: [
      {
        text: "Custom normalized eight-point fundamental-matrix estimation",
        links: [
          {
            term: "normalized eight-point",
            url: "https://doi.org/10.1109/34.601246",
          },
        ],
      },
      {
        text: "RANSAC rejects geometrically inconsistent correspondences",
        links: [
          {
            term: "RANSAC",
            url: "https://doi.org/10.1145/358669.358692",
          },
        ],
      },
      {
        text: "Sampson error measures agreement with the estimated epipolar geometry",
        links: [
          {
            term: "Sampson error",
            url: "https://doi.org/10.1016/0146-664X(82)90101-0",
          },
        ],
      },
      {
        text: "The visualization samples the RANSAC consensus set and draws the corresponding epipolar constraints",
        links: [
          {
            term: "RANSAC",
            url: "https://doi.org/10.1145/358669.358692",
          },
        ],
      },
    ],
    pipeline: [
      "Correspondences",
      "RANSAC",
      "Fundamental Matrix",
      "Inlier Geometry",
    ],
  },

  reconstruction: {
    "point-cloud": {
      eyebrow: "TWO-VIEW RECONSTRUCTION · 3D",
      title: "Sparse & Semi-Dense Point Cloud",
      description:
        "Geometrically consistent image correspondences are projected into 3D using the recovered two-view geometry. Sparse feature matches establish the scene structure, while a semi-dense pass recovers additional points from image regions suitable for tracking.",
      implementation: [
        "Relative camera pose is recovered from two-view geometry",
        "Correspondences are triangulated into 3D",
        "Positive-depth and reprojection checks reject invalid points",
        {
          text: "Lucas-Kanade optical flow contributes semi-dense correspondences beyond the sparse ORB matches",
          links: [
            {
              term: "Lucas-Kanade",
              url: "https://www.ri.cmu.edu/pub_files/pub3/lucas_bruce_d_1981_2/lucas_bruce_d_1981_2.pdf",
            },
            {
              term: "ORB",
              url: "https://ieeexplore.ieee.org/document/6126544/",
            },
          ],
        },
        "Camera poses and coordinate axes can be inspected directly in the 3D viewer",
      ],
      pipeline: ["Geometry", "Camera Pose", "Triangulation", "3D Points"],
    },

    dense: {
      eyebrow: "TWO-VIEW RECONSTRUCTION · DENSE",
      title: "Dense Stereo Reconstruction",
      description:
        "Dense stereo searches for correspondence across the image rather than only at selected feature points. After rectification, matching is constrained primarily to horizontal scanlines; accepted disparities are filtered, converted into 3D geometry, and connected into a textured surface.",
      implementation: [
        {
          text: "Custom WebGPU dense stereo matcher with CPU fallback",
          links: [
            {
              term: "WebGPU",
              url: "https://webgpu.org/",
            },
          ],
        },
        {
          text: "ZNCC patch similarity scores candidate dense correspondences",
          links: [
            {
              term: "ZNCC",
              url: "https://doi.org/10.1016/j.image.2017.01.001",
            },
          ],
        },
        "Ambiguity filtering rejects matches without a sufficiently distinct optimum",
        "Bidirectional consistency compares forward and reverse correspondence estimates",
        "Filtered disparity is converted into scene geometry",
        {
          text: "Neighboring valid samples are triangulated into a textured Three.js mesh",
          links: [
            {
              term: "Three.js",
              url: "https://threejs.org/",
            },
          ],
        },
      ],
      pipeline: [
        "Rectified Pair",
        "WebGPU Stereo",
        "Filtered Disparity",
        "3D Geometry",
        "Textured Mesh",
      ],
    },

    disparity: {
      eyebrow: "TWO-VIEW RECONSTRUCTION · DISPARITY",
      title: "Dense Disparity",
      description:
        "Disparity records how far corresponding image locations shift between the rectified stereo views. For a calibrated stereo pair, this image-space displacement is inversely related to depth: larger disparities generally represent nearby geometry and smaller disparities represent more distant geometry.",
      implementation: [
        {
          text: "Computed by the same custom WebGPU stereo matcher used for dense reconstruction",
          links: [
            {
              term: "WebGPU",
              url: "https://webgpu.org/",
            },
          ],
        },
        {
          text: "ZNCC scores candidate horizontal correspondences along the stereo search range",
          links: [
            {
              term: "ZNCC",
              url: "https://doi.org/10.1016/j.image.2017.01.001",
            },
          ],
        },
        "Confidence contributes to the displayed disparity intensity",
        "Invalid or rejected disparities remain excluded from reconstruction",
      ],
      pipeline: [
        "Rectified Images",
        "Correspondence Search",
        "Disparity",
        "Depth",
      ],
    },

    "match-status": {
      eyebrow: "TWO-VIEW RECONSTRUCTION · DIAGNOSTICS",
      title: "Dense Match Diagnostics",
      description:
        "The diagnostic view exposes the filtering decisions behind the final disparity map. Each evaluated location is classified by the stage at which its candidate correspondence was accepted or rejected, making failure modes in the dense matcher directly inspectable.",
      implementation: [
        "Green: accepted correspondence",
        {
          text: "Red: ZNCC similarity below the acceptance threshold",
          links: [
            {
              term: "ZNCC",
              url: "https://doi.org/10.1016/j.image.2017.01.001",
            },
          ],
        },
        "Yellow: competing disparities make the match ambiguous",
        "Blue: reverse correspondence search rejected the candidate",
        "Purple: left-right correspondence consistency failed",
      ],
      pipeline: [
        "Candidate Match",
        "Score Test",
        "Ambiguity Test",
        "Reverse Check",
        "Accept / Reject",
      ],
    },
  },
};

function parseReconstructionRoute(route) {
  if (!route) {
    return {
      stage: "input",
      view: "dense",
    };
  }

  const [stagePart, viewPart] = route.split("/");
  const stage = RECONSTRUCTION_STAGES.has(stagePart) ? stagePart : "input";
  const view =
    stage === "reconstruction" && RECONSTRUCTION_VIEWS.has(viewPart)
      ? viewPart
      : "dense";

  return {
    stage,
    view,
  };
}

export default function ReconstructionDemo({
  Panel,
  Control,
  Slider,
  Stat,
  route,
  onNavigate,
  onInfoChange,
}) {
  const initialRoute = parseReconstructionRoute(route);
  const [stage, setStage] = useState(initialRoute.stage);
  const [reconstructionView, setReconstructionView] = useState(
    initialRoute.view,
  );
  const [cvStatus, setCvStatus] = useState("Loading OpenCV...");

  // Settings
  const [featureCount, setFeatureCount] = useState(
    DEFAULT_SETTINGS.featureCount,
  );
  const [ratioThreshold, setRatioThreshold] = useState(
    DEFAULT_SETTINGS.ratioThreshold,
  );

  // Inputs
  const [selectedInput, setSelectedInput] = useState(null);
  const [activeInput, setActiveInput] = useState(null);
  const [activeSettings, setActiveSettings] = useState(DEFAULT_SETTINGS);
  const [selectedSceneId, setSelectedSceneId] = useState(
    "kitti_2011_09_26_0000000000",
  );
  const [datasetStatus, setDatasetStatus] = useState("Loading...");
  const [pendingImageA, setPendingImageA] = useState(null);
  const [pendingImageB, setPendingImageB] = useState(null);
  const [runId, setRunId] = useState(0);

  useEffect(() => {
    if (!onInfoChange) {
      return;
    }

    if (stage === "reconstruction") {
      onInfoChange(
        RECONSTRUCTION_INFO.reconstruction[reconstructionView] ??
          RECONSTRUCTION_INFO.reconstruction.dense,
      );
      return;
    }

    onInfoChange(RECONSTRUCTION_INFO[stage] ?? RECONSTRUCTION_INFO.input);
  }, [stage, reconstructionView, onInfoChange]);

  useEffect(() => {
    const parsed = parseReconstructionRoute(route);
    setStage(parsed.stage);
    setReconstructionView(parsed.view);
  }, [route]);

  // OpenCV initialization
  useEffect(() => {
    let cancelled = false;

    async function initializeOpenCV() {
      try {
        const cv = await getOpenCV();

        if (cancelled) {
          return;
        }

        debugLog("[Reconstruction] OpenCV ready:", cv);
        setCvStatus("Ready");
      } catch (error) {
        if (cancelled) {
          return;
        }

        console.error("[Reconstruction] OpenCV initialization failed:", error);
        setCvStatus("OpenCV failed");
      }
    }

    initializeOpenCV();

    return () => {
      cancelled = true;
    };
  }, []);

  // Dataset loading
  useEffect(() => {
    let cancelled = false;

    async function loadSelectedDataset() {
      try {
        setDatasetStatus("Loading...");

        const isKITTI = selectedSceneId.startsWith("kitti_");
        const scene = isKITTI
          ? await loadKITTIScene(selectedSceneId)
          : await loadETH3DScene(selectedSceneId);

        if (cancelled) {
          return;
        }

        const stagedInput = {
          mode: "dataset",
          sceneId: scene.id,
          name: scene.name,
          dataset: isKITTI ? "KITTI" : "ETH3D",
          imageA: {
            file: null,
            url: scene.imageA,
          },
          imageB: {
            file: null,
            url: scene.imageB,
          },
          calibration: scene.calibration,
        };

        setSelectedInput(stagedInput);
        setDatasetStatus(scene.name);

        debugLog("[Reconstruction] Dataset scene staged:", {
          dataset: stagedInput.dataset,
          scene: scene.id,
          width: scene.calibration.width,
          height: scene.calibration.height,
          baseline: scene.calibration.baseline,
        });
      } catch (error) {
        if (cancelled) {
          return;
        }

        console.error("[Reconstruction] Dataset loading failed:", error);
        setSelectedInput(null);
        setDatasetStatus("Failed");
      }
    }

    loadSelectedDataset();

    return () => {
      cancelled = true;
    };
  }, [selectedSceneId]);

  const pipeline = useReconstructionPipeline({
    imageA: activeInput?.imageA ?? null,
    imageB: activeInput?.imageB ?? null,
    calibration: activeInput?.calibration ?? null,
    inputMode: activeInput?.mode ?? null,
    featureCount: activeSettings.featureCount,
    ratioThreshold: activeSettings.ratioThreshold,
    runId,
  });

  const {
    featureStatus,
    featuresA,
    featuresB,
    matches,
    geometry,
    reconstruction,
    semiDenseReconstruction,
    denseTracks,
    filteredDenseDisparity,
    denseMesh,
    denseStatus,
  } = pipeline;

  const customPairReady = Boolean(pendingImageA && pendingImageB);

  const inlierRatio =
    geometry && matches.length > 0
      ? (geometry.inliers.length / matches.length) * 100
      : null;

  let validDisparityCount = null;

  if (filteredDenseDisparity) {
    validDisparityCount = 0;

    for (const disparity of filteredDenseDisparity.disparities) {
      if (Number.isFinite(disparity)) {
        validDisparityCount += 1;
      }
    }
  }

  const stagedInput = customPairReady
    ? {
        mode: "custom",
        name: "Custom pair",
        dataset: null,
        imageA: pendingImageA,
        imageB: pendingImageB,
        calibration: null,
      }
    : selectedInput;

  const canRun =
    cvStatus === "Ready" &&
    Boolean(stagedInput?.imageA?.url && stagedInput?.imageB?.url);

  const inputChanged =
    Boolean(activeInput) &&
    (stagedInput?.mode !== activeInput.mode ||
      stagedInput?.sceneId !== activeInput.sceneId ||
      stagedInput?.imageA?.url !== activeInput.imageA?.url ||
      stagedInput?.imageB?.url !== activeInput.imageB?.url ||
      featureCount !== activeSettings.featureCount ||
      ratioThreshold !== activeSettings.ratioThreshold);

  function navigateStage(nextStage) {
    setStage(nextStage);

    if (nextStage === "reconstruction") {
      onNavigate?.(`reconstruction/${reconstructionView}`);
      return;
    }

    onNavigate?.(nextStage);
  }

  function handleReconstructionViewChange(view) {
    setReconstructionView(view);

    if (stage === "reconstruction") {
      onNavigate?.(`reconstruction/${view}`);
    }
  }

  function handleImageAChange(image) {
    setPendingImageA(image);
  }

  function handleImageBChange(image) {
    setPendingImageB(image);
  }

  function handleRunReconstruction() {
    if (!canRun) {
      return;
    }

    setActiveInput({
      ...stagedInput,
      imageA: { ...stagedInput.imageA },
      imageB: { ...stagedInput.imageB },
    });

    setActiveSettings({
      featureCount,
      ratioThreshold,
    });

    setRunId((value) => value + 1);
    setStage("features");
  }

  const displayedImageA =
    stage === "input"
      ? (stagedInput?.imageA ?? null)
      : (activeInput?.imageA ?? stagedInput?.imageA ?? null);

  const displayedImageB =
    stage === "input"
      ? (stagedInput?.imageB ?? null)
      : (activeInput?.imageB ?? stagedInput?.imageB ?? null);

  const displayedCalibration =
    activeInput?.calibration ?? stagedInput?.calibration ?? null;

  const displayedMode = stagedInput?.mode ?? activeInput?.mode ?? "dataset";

  return (
    <div className="reconstruction-workspace">
      <aside className="reconstruction-sidebar">
        <Panel title="PIPELINE">
          <button
            type="button"
            className={
              stage === "input"
                ? "reconstruction-button active"
                : "reconstruction-button"
            }
            onClick={() => navigateStage("input")}
          >
            1. INPUT
          </button>

          <button
            type="button"
            className={
              stage === "features"
                ? "reconstruction-button active"
                : "reconstruction-button"
            }
            disabled={!activeInput}
            onClick={() => navigateStage("features")}
          >
            2. FEATURES
          </button>

          <button
            type="button"
            className={
              stage === "matches"
                ? "reconstruction-button active"
                : "reconstruction-button"
            }
            disabled={!activeInput}
            onClick={() => navigateStage("matches")}
          >
            3. MATCHES
          </button>

          <button
            type="button"
            className={
              stage === "epipolar"
                ? "reconstruction-button active"
                : "reconstruction-button"
            }
            disabled={!geometry}
            onClick={() => navigateStage("epipolar")}
          >
            4. EPIPOLAR GEOMETRY
          </button>

          <button
            type="button"
            className={
              stage === "reconstruction"
                ? "reconstruction-button active"
                : "reconstruction-button"
            }
            disabled={!reconstruction || denseStatus !== "complete"}
            onClick={() => navigateStage("reconstruction")}
          >
            5. RECONSTRUCTION
          </button>
        </Panel>

        <Panel title="INPUT">
          <div className="reconstruction-info">
            <p>
              <strong>Mode</strong>
              <span>
                {displayedMode === "dataset"
                  ? "Calibrated dataset"
                  : "Custom pair"}
              </span>
            </p>
          </div>

          <label className="reconstruction-info">
            <span>Scene</span>
            <select
              value={selectedSceneId}
              onChange={(event) => {
                setSelectedSceneId(event.target.value);
                setPendingImageA(null);
                setPendingImageB(null);
              }}
              className="reconstruction-select"
            >
              {DATASET_SCENES.map((scene) => (
                <option key={scene.id} value={scene.id}>
                  {scene.name}
                </option>
              ))}
            </select>
          </label>

          <button
            type="button"
            className="reconstruction-button"
            disabled={!canRun}
            onClick={handleRunReconstruction}
          >
            RUN RECONSTRUCTION
          </button>

          {inputChanged && (
            <div className="reconstruction-info">
              <p>
                <strong>Status</strong>
                <span>Input/settings changed — run again</span>
              </p>
            </div>
          )}
        </Panel>

        <Panel title="FEATURES">
          <Slider
            label="ORB Features"
            value={featureCount}
            min={250}
            max={4000}
            step={250}
            display={featureCount.toLocaleString()}
            onChange={setFeatureCount}
          />

          <Slider
            label="Ratio Threshold"
            value={ratioThreshold}
            min={0.5}
            max={0.95}
            step={0.01}
            display={ratioThreshold.toFixed(2)}
            onChange={setRatioThreshold}
          />
        </Panel>

        <Panel title="RESULTS">
          <Stat label="OpenCV" value={cvStatus} />

          <Stat
            label="Dataset"
            value={activeInput?.name ?? stagedInput?.name ?? datasetStatus}
          />

          <Stat
            label="Calibration"
            value={
              displayedCalibration
                ? "Known intrinsics"
                : activeInput
                  ? "Estimated"
                  : "—"
            }
          />

          <Stat
            label="Feature Extraction"
            value={activeInput ? featureStatus : "Waiting"}
          />

          <Stat
            label="Features A"
            value={
              featuresA ? featuresA.keypoints.length.toLocaleString() : "—"
            }
          />

          <Stat
            label="Features B"
            value={
              featuresB ? featuresB.keypoints.length.toLocaleString() : "—"
            }
          />

          <Stat
            label="Matches"
            value={
              featuresA && featuresB ? matches.length.toLocaleString() : "—"
            }
          />

          <Stat
            label="Inliers"
            value={geometry ? geometry.inliers.length.toLocaleString() : "—"}
          />

          <Stat
            label="Inlier Ratio"
            value={inlierRatio !== null ? `${inlierRatio.toFixed(1)}%` : "—"}
          />

          <Stat
            label="Dense Tracks"
            value={
              denseTracks.length > 0 ? denseTracks.length.toLocaleString() : "—"
            }
          />

          <Stat
            label="Dense Stereo"
            value={
              denseStatus === "complete"
                ? "Ready"
                : denseStatus === "running"
                  ? "Processing..."
                  : denseStatus === "error"
                    ? "Failed"
                    : "Waiting"
            }
          />

          <Stat
            label="Valid Disparities"
            value={
              validDisparityCount !== null
                ? validDisparityCount.toLocaleString()
                : "—"
            }
          />

          <Stat
            label="Mesh Vertices"
            value={denseMesh ? denseMesh.vertexCount.toLocaleString() : "—"}
          />

          <Stat
            label="Mesh Triangles"
            value={denseMesh ? denseMesh.triangleCount.toLocaleString() : "—"}
          />
        </Panel>

        <Panel title="IMPLEMENTATION">
          <div className="implementation-list">
            <div className="implementation-item">
              <span className="implementation-key">FEATURES</span>
              <span className="implementation-value">OpenCV.js · ORB</span>
            </div>

            <div className="implementation-item">
              <span className="implementation-key">MATCHING</span>
              <span className="implementation-value">
                Hamming distance · Ratio test
              </span>
            </div>

            <div className="implementation-item">
              <span className="implementation-key">GEOMETRY</span>
              <span className="implementation-value">
                Custom implementation
              </span>
            </div>

            <div className="implementation-item">
              <span className="implementation-key">DENSE TRACKING</span>
              <span className="implementation-value">
                Tomasi–Kanade + Lucas–Kanade
              </span>
            </div>
          </div>
        </Panel>
      </aside>

      <main className="reconstruction-viewer">
        <MatchView
          stage={stage}
          imageA={displayedImageA}
          imageB={displayedImageB}
          pendingImageA={pendingImageA}
          pendingImageB={pendingImageB}
          featuresA={featuresA}
          featuresB={featuresB}
          matches={matches}
          geometry={geometry}
          reconstruction={reconstruction}
          semiDenseReconstruction={semiDenseReconstruction}
          denseDisparity={filteredDenseDisparity}
          denseMesh={denseMesh}
          onImageAChange={handleImageAChange}
          onImageBChange={handleImageBChange}
          onRunCustomReconstruction={handleRunReconstruction}
          reconstructionView={reconstructionView}
          onReconstructionViewChange={handleReconstructionViewChange}
        />
      </main>
    </div>
  );
}
